import Box from "@mui/material/Box";
import MobileStepper from "@mui/material/MobileStepper";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import { ApiErrorDisplayAccordion } from "core/components/ApiErrorDisplayAccordion";
import { BackButton } from "core/components/BackButton";
import { NextButton } from "core/components/NextButton";
import { PageContainer } from "core/components/PageContainer";
import { PageHeader } from "core/components/PageHeader";
import { SubmitButton } from "core/components/SubmitButton";
import type { TFunction } from "core/entities";
import type { RemoteServiceErrorResponse } from "core/entities/APITypes";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import { useCentrixFetchPublic } from "core/hooks/useCentrixFetch";
import { GreCaptchaProvider, useGReCaptcha } from "core/hooks/useGReCaptcha";
import { useLocale } from "core/hooks/useLocale";
import { useSynchronizeErrorLanguage } from "core/hooks/useSynchronizeErrorLanguage";
import { useToaster } from "core/hooks/useToaster";
import { uuid } from "core/utils/uuid";
import type {
  EstimateRequestDto,
  EstimateRequestFormMethods,
} from "features/EstimateRequest/entities";
import Head from "next/head";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PublicEstimateRequestConfirmAndSubmit } from "./components/PublicEstimateRequestConfirmAndSubmit";
import {
  PublicEstimateRequestCustomerInformationForm,
  usePublicEstimateRequestCustomerInformationFormMethods,
} from "./components/PublicEstimateRequestCustomerInformationForm";
import {
  PublicEstimateRequestDamageInformationForm,
  usePublicEstimateRequestDamageInformationFormMethods,
} from "./components/PublicEstimateRequestDamageInformationForm";
import { PublicEstimateRequestSubmitSuccess } from "./components/PublicEstimateRequestSubmitSuccess";
import {
  PublicEstimateRequestVehicleInformationForm,
  usePublicEstimateRequestVehicleInformationFormFormMethods,
} from "./components/PublicEstimateRequestVehicleInformationForm";
import { useInsurerPayActive } from "./components/useInsurerPayActive";
import { mapEstimateRequestFormValuesToEstimateRequestCreateDto } from "./utils/mapEstimateRequestFormValuesToEstimateRequestCreateDto";

export function PublicEstimateRequestPage() {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:EstimateRequestPage",
  });
  return (
    <PageContainer>
      <PageHeader title={t("pageTitle")} />
      <GreCaptchaProvider>
        <PublicEstimateRequestContent t={t} />
      </GreCaptchaProvider>
    </PageContainer>
  );
}

function PublicEstimateRequestContent({ t }: { t: TFunction }) {
  const { isMobile } = useBreakpoints();
  const locale = useLocale();
  const centrixFetch = useCentrixFetchPublic();
  const [estimateRequestId] = useState(() => uuid());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { errorToast, successToast } = useToaster();
  const [createdEstimateRequest, setCreatedEstimateRequest] =
    useState<EstimateRequestDto | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  const submitErrorMessage = t("submitErrorMessage");

  const vehicleInformationFormMethods =
    usePublicEstimateRequestVehicleInformationFormFormMethods();
  useSynchronizeErrorLanguage(vehicleInformationFormMethods);

  const customerInformationFormMethods =
    usePublicEstimateRequestCustomerInformationFormMethods();
  useSynchronizeErrorLanguage(customerInformationFormMethods);

  const shopId = customerInformationFormMethods.watch("shopId");
  const isInsurerPayActive = useInsurerPayActive(shopId);

  const damageInformationFormMethods =
    usePublicEstimateRequestDamageInformationFormMethods();
  useSynchronizeErrorLanguage(damageInformationFormMethods);

  const forms: EstimateRequestFormMethods = {
    vehicleInformationFormMethods,
    customerInformationFormMethods,
    damageInformationFormMethods,
  };
  const invalidFormMessage = t("invalidFormMessage");
  const recaptchaNotLoaded = t("recaptchaNotLoaded");
  const recaptchaNotGenerated = t("recaptchaNotGenerated");
  const recaptcha = useGReCaptcha();

  const [errorResponse, setErrorResponse] =
    useState<RemoteServiceErrorResponse | null>(null);

  async function submitEstimateRequest() {
    setErrorResponse(null);
    if (recaptcha.isLoading) {
      errorToast(recaptchaNotLoaded);
      return;
    }
    setIsSubmitting(true);
    const areEstimateRequestFormsValid = (
      await Promise.allSettled([
        forms.vehicleInformationFormMethods.trigger(),
        forms.customerInformationFormMethods.trigger(),
        forms.damageInformationFormMethods.trigger(),
      ])
    ).every((result) => result.status === "fulfilled" && result.value);

    if (!areEstimateRequestFormsValid) {
      errorToast(invalidFormMessage);
      setIsSubmitting(false);
      return;
    }
    const vehicleInformation = vehicleInformationFormMethods.getValues();
    const customerInformation = customerInformationFormMethods.getValues();
    const damageInformation = damageInformationFormMethods.getValues();

    const recaptchaToken = await recaptcha
      .execute("public_estimate_request_submit")
      .catch(() => null);

    if (!recaptchaToken) {
      errorToast(recaptchaNotGenerated);
      setIsSubmitting(false);
      return;
    }
    const postData = mapEstimateRequestFormValuesToEstimateRequestCreateDto({
      vehicleInformation,
      customerInformation,
      damageInformation,
      recaptchaToken,
      locale: locale as string,
    });

    const requestResponse = await centrixFetch({
      method: "post",
      path: "/api/app/estimate-request",
      body: postData,
    });

    if (!requestResponse.ok) {
      const errorData = await requestResponse.json();
      setIsSubmitting(false);
      setErrorResponse(errorData);
      errorToast(submitErrorMessage);
      return;
    }

    const requestResponseData = await requestResponse.json();
    successToast(t("successToastMessage"));
    setIsSubmitting(false);
    setCreatedEstimateRequest(requestResponseData);
  }

  const steps = [
    {
      label: t("stepLabel.customerInformation"),
      formMethods: customerInformationFormMethods,
      Component: (
        <PublicEstimateRequestCustomerInformationForm
          t={t}
          formMethods={customerInformationFormMethods}
          resetImages={() => {
            damageInformationFormMethods.setValue("damageLocations", []);
          }}
        />
      ),
    },
    {
      label: t("stepLabel.vehicleInformation"),
      formMethods: vehicleInformationFormMethods,
      Component: (
        <PublicEstimateRequestVehicleInformationForm
          t={t}
          formMethods={vehicleInformationFormMethods}
          estimateRequestId={estimateRequestId}
          shopId={shopId}
          isInsurerPayActive={isInsurerPayActive}
        />
      ),
    },
    {
      label: t("stepLabel.damageInformation"),
      formMethods: damageInformationFormMethods,
      Component: (
        <PublicEstimateRequestDamageInformationForm
          shopId={shopId}
          requestId={estimateRequestId}
          t={t}
          formMethods={damageInformationFormMethods}
        />
      ),
    },
    {
      label: t("stepLabel.confirmAndSubmit"),
      Component: (
        <PublicEstimateRequestConfirmAndSubmit
          damageInformationFormMethods={damageInformationFormMethods}
          customerInformationFormMethods={customerInformationFormMethods}
          vehicleInformationFormMethods={vehicleInformationFormMethods}
          errorSlot={
            <ApiErrorDisplayAccordion
              errorResponse={errorResponse}
              variant="elevation"
              clearError={() => {
                setErrorResponse(null);
              }}
            />
          }
          t={t}
        />
      ),
    },
  ];

  const onNextClick = async () => {
    const currentFormMethods = steps[activeStep]?.formMethods;
    if (!currentFormMethods) {
      return;
    }
    await currentFormMethods.handleSubmit(() => {
      setActiveStep((step) => Math.min(step + 1, steps.length - 1));
    })();
  };

  const onBackClick = () => {
    setActiveStep((step) => Math.max(step - 1, 0));
  };

  if (createdEstimateRequest) {
    return <PublicEstimateRequestSubmitSuccess t={t} />;
  }

  if (isMobile) {
    return (
      <>
        <Head>
          <style>
            {`
            .grecaptcha-badge {
              bottom: 64px !important;
            }
          `}
          </style>
        </Head>
        <Typography variant="h6" component="h2" mb={4}>
          {steps[activeStep]?.label}
        </Typography>
        <Box sx={{ mb: 17 }}>{steps[activeStep]?.Component}</Box>
        <ApiErrorDisplayAccordion
          errorResponse={errorResponse}
          variant="elevation"
        />
        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1,
          }}
          elevation={8}
        >
          <MobileStepper
            variant="dots"
            sx={{ backgroundColor: "background.paper" }}
            steps={steps.length}
            activeStep={activeStep}
            nextButton={
              activeStep < steps.length - 1 ? (
                <NextButton onClick={onNextClick} />
              ) : (
                <SubmitButton
                  loading={isSubmitting}
                  type="button"
                  onClick={submitEstimateRequest}
                />
              )
            }
            backButton={
              <BackButton disabled={activeStep === 0} onClick={onBackClick} />
            }
          />
        </Paper>
      </>
    );
  }

  return (
    <>
      <Stepper
        sx={{ marginBlockEnd: 4 }}
        activeStep={activeStep}
        alternativeLabel
      >
        {steps.map(({ label }) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box mb={3}>
        {steps[activeStep]?.Component}
        <Stack mt={3} direction="row" justifyContent="center" gap={2}>
          <BackButton disabled={activeStep === 0} onClick={onBackClick} />
          {activeStep < steps.length - 1 ? (
            <NextButton onClick={onNextClick} />
          ) : (
            <SubmitButton
              loading={isSubmitting}
              type="button"
              onClick={submitEstimateRequest}
            />
          )}
        </Stack>
      </Box>
    </>
  );
}
